// Eskom PARCC — the CES (Compliance Evaluation Sheet) capture mutation.
//
// One coherent save that fans a normalised evaluation out across the four SQL
// tables, in the ORDER the write layer requires (CES rows carry a workingPaperId,
// so a working paper must exist first):
//
//   1. upsertTransaction      — insert-or-update the transaction (rollup + review fields)
//   2. upsertWorkingPaperForTx — insert-or-update the working paper (CES rows depend on it)
//   3. replaceCesRowsForTx    — delete-then-reinsert the per-criterion rows
//   4. reconcileFindingsForTx — reconcile the findings (keep/update/close/insert), so a
//                               criterion the buyer fixed is CLOSED, not forgotten (RTM WS-I)
//
// Calling the dbWrite wrappers DIRECTLY in sequence (rather than composing the
// four single-table hooks) gives ONE error/toast path and ONE invalidation block,
// and guarantees the await order — composing four independent hooks would risk
// firing out of order or surfacing up to four separate toasts. Errors are NOT
// swallowed: onError surfaces a single toast AND the hook keeps `error`/`isError`.
import { useMutation } from '@tanstack/react-query';
import { queryClient } from '../lib/queryClient';
import { invalidateRegister } from '../lib/queryKeys';
import { useToast } from '../components/ui/Toast';
import {
  upsertTransaction,
  upsertWorkingPaperForTx,
  replaceCesRowsForTx,
  reconcileFindingsForTx,
  insertEvaluationSheetBackup,
  recordAudit,
  withAuditSuppressed,
  type TransactionInsertInput,
  type WorkingPaperInput,
  type CesRowInput,
  type FindingInsertInput,
} from '../lib/dbWrite';

/** The payload a single CES capture persists — the output of `buildCesCapture`. */
export interface CaptureCesArgs {
  referenceNo: string;
  transaction: TransactionInsertInput;
  workingPaper: WorkingPaperInput;
  cesRows: CesRowInput[];
  findings: FindingInsertInput[];
  /** The audit action + backup source; the workbook upload stamps `ces_upload`-flavoured notes. */
  auditNote?: string;
  sourceAction?: 'ces_capture' | 'ces_upload';
}

/**
 * Persist ONE complete CES capture across the four tables in the required
 * order — the shared engine behind the capture page's mutation AND the
 * workbook upload's per-row loop (R5 #1). No toast, no query invalidation:
 * callers own their error surface and invalidate once per batch.
 */
export async function performCesCapture({
  referenceNo,
  transaction,
  workingPaper,
  cesRows,
  findings,
  auditNote,
  sourceAction = 'ces_capture',
}: CaptureCesArgs): Promise<void> {
  // A CES capture is ONE logical change spanning four tables. Suppress the
  // per-sub-write audit entries so the trail records a single `ces_capture`
  // (below) rather than four rows; the actor on it is server-stamped.
  const { id: transactionId } = await withAuditSuppressed(async () => {
    // 1. Transaction first — its referenceNo is the join key everything resolves on.
    const result = await upsertTransaction(transaction);
    // 2. Working paper next — CES rows carry its id, so it must exist before step 3.
    await upsertWorkingPaperForTx(referenceNo, workingPaper);
    // 3. CES rows — delete-then-reinsert against the now-present working paper.
    await replaceCesRowsForTx(referenceNo, cesRows);
    // 4. Findings — RECONCILE (keep/update/close/insert): a criterion no longer flagged
    //    is closed via 'buyer-fix' (kept, traceable), not deleted (RTM Workstream I).
    await reconcileFindingsForTx(referenceNo, findings);
    return result;
  });

  // 5. One audit entry for the whole capture (best-effort, server-stamped actor).
  await recordAudit({
    action: 'ces_capture',
    entityType: 'Transaction',
    entityId: referenceNo,
    note: auditNote ?? `${cesRows.length} CES row(s), ${findings.length} finding(s)`,
  });

  // 6. BEST-EFFORT audit snapshot — a versioned EvaluationSheetBackup capturing
  //    the persisted evaluation. Wrapped so an audit failure NEVER fails or
  //    undoes the capture above (the four primary writes have already settled);
  //    it only console.warns. Not a second round-trip on a hot path — one extra
  //    insert per CES save is acceptable.
  try {
    await insertEvaluationSheetBackup({
      transactionId,
      reviewStatus: workingPaper.reviewStatus,
      sourceAction,
      fullEvaluation: { cesRows, findings },
      workingPaper,
    });
  } catch (auditError) {
    console.warn('CES evaluation-sheet backup failed (non-blocking):', auditError);
  }
}

/**
 * Persist a complete CES capture across the four tables in the required order.
 * The four awaits run sequentially (never in parallel): the working paper MUST
 * exist before its CES rows, and a partial failure stops the sequence at the
 * failing step (surfaced via the toast + the hook's error state).
 */
export function useCaptureCes() {
  const { show } = useToast();
  return useMutation({
    mutationFn: performCesCapture,
    onError: (error: unknown) => {
      show(
        error instanceof Error ? error.message : 'Failed to save the compliance evaluation.',
        'error',
      );
    },
    onSettled: () => {
      // Invalidate on SETTLED (not just onSuccess): a PARTIAL failure (e.g. tx +
      // working paper written, then CES rows fail) still mutated some tables, so
      // the user must refetch to see the actual persisted state rather than a
      // stale or falsely-successful view.
      //
      // invalidateRegister covers the broad keys — each entity's byRef/byTx key
      // shares its prefix, so the narrower keys are matched too — AND the
      // reviewer-scoped `['register','mine',…]` caches (the hard-refresh bug).
      invalidateRegister(queryClient);
    },
  });
}
